<template>
  <ion-header>
    <ion-toolbar>
      <ion-buttons slot="start">
        <ion-button @click="cancelButton">{{ cancelLabel }}</ion-button>
      </ion-buttons>
      <ion-title>{{ title }}</ion-title>
      <ion-buttons slot="end">
        <ion-button @click="doneButton">{{ confirmLabel }}</ion-button>
      </ion-buttons>
    </ion-toolbar>
    <ion-toolbar>
      <ion-segment v-model="viewMode">
        <ion-segment-button value="list">
          <ion-label>List</ion-label>
        </ion-segment-button>
        <ion-segment-button value="tree">
          <ion-label>Tree</ion-label>
        </ion-segment-button>
      </ion-segment>
    </ion-toolbar>
  </ion-header>

  <ion-content v-if="viewMode === 'list'" class="ion-padding">
    <div v-if="description || errorMessage" class="modal-copy">
      <p v-if="description" class="description">{{ description }}</p>
      <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    </div>
    <ion-searchbar v-model="search" />
    <ion-radio-group v-model="chosenContainer" allow-empty-selection>
      <ion-item
        v-if="allowRoot"
        button
        :detail="false"
        class="list-item root-item"
        @click="selectRoot"
      >
        <ion-radio slot="start" :value="null"></ion-radio>
        <ion-icon slot="start" :icon="homeOutline" />
        <ion-label>Root Level</ion-label>
      </ion-item>
      <RecycleScroller
        class="scroller"
        :items="filteredContainersWithId"
        :item-size="60"
        key-field="id"
        v-slot="{ item }"
      >
        <ChooseContainerListElement
          class="scroll-item"
          :container="item"
          @select="chosenContainer = $event"
        />
      </RecycleScroller>
    </ion-radio-group>
  </ion-content>

  <ion-content v-else>
    <div v-if="description || errorMessage" class="modal-copy tree-copy ion-padding-horizontal ion-padding-top">
      <p v-if="description" class="description">{{ description }}</p>
      <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    </div>
    <div
      v-if="allowRoot"
      class="root-selector ion-padding-horizontal"
    >
      <div
        class="column-item root-pill"
        :class="{ 'is-selected': chosenContainer === null }"
        @click="selectRoot"
      >
        <ion-icon :icon="homeOutline" class="item-icon" />
        <span class="item-name">Root Level</span>
      </div>
    </div>
    <div class="column-view" ref="columnViewRef">
      <div
        v-for="(columnItems, level) in columns"
        :key="level"
        class="column"
      >
        <div
          v-for="container in columnItems"
          :key="container.object.id"
          class="column-item"
          :class="{
            'is-selected': selectedPath[level] === container.object.id,
            'in-path': selectedPath[level] === container.object.id && level < selectedPath.length - 1,
            'has-children': hasChildren(container),
          }"
          @click="selectAtLevel(level, container)"
        >
          <ApiIcon
            v-if="container.object.logo"
            :type="container.object.logo.type"
            :name="container.object.logo.name"
            :iconStyle="container.object.logo.style"
            class="item-icon"
          />
          <ApiIcon v-else type="fa" name="box" iconStyle="solid" class="item-icon" />
          <span class="item-name">{{ container.object.name }}</span>
          <ion-icon v-if="hasChildren(container)" :icon="chevronForward" class="chevron" />
        </div>
      </div>
    </div>
  </ion-content>

  <ion-footer v-if="footerConfirmLabel">
    <ion-toolbar>
      <ion-button expand="block" class="footer-action" @click="doneButton">
        {{ footerConfirmLabel }}
      </ion-button>
    </ion-toolbar>
  </ion-footer>
</template>

<script setup lang="ts">
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonRadioGroup,
  IonButtons,
  IonButton,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonIcon,
  IonItem,
  IonRadio,
} from "@ionic/vue";
import { chevronForward, homeOutline } from "ionicons/icons";
import { computed, defineComponent, nextTick, ref, watch } from "vue";
import ChooseContainerListElement from "@/components/ChooseContainerListElement.vue";
import ApiIcon from "@/components/ApiIcon.vue";
import type { ApiObjectWithReferences } from "@/models/api";
import { filterContainers } from "@/helper/filterContainers";
import { useApiStore } from "@/stores/api";
import { OBJECT_TYPE_CONTAINER } from "@/data/constants";

const api = useApiStore();

defineComponent({
  name: "ChooseContainerModal",
});
const props = withDefaults(
  defineProps<{
    modelValue: ApiObjectWithReferences | null;
    title?: string;
    confirmLabel?: string;
    footerConfirmLabel?: string | null;
    cancelLabel?: string;
    description?: string | null;
    errorMessage?: string | null;
    allowRoot?: boolean;
    excludeIds?: number[];
  }>(),
  {
    title: "Choose Container",
    confirmLabel: "Done",
    footerConfirmLabel: null,
    cancelLabel: "Cancel",
    description: null,
    errorMessage: null,
    allowRoot: false,
    excludeIds: () => [],
  }
);
const emit = defineEmits<{
  (e: "update:modelValue", value: ApiObjectWithReferences | null): void;
  (e: "save", value: ApiObjectWithReferences | null): void;
  (e: "close"): void;
}>();

const search = ref("");
const viewMode = ref<"list" | "tree">("tree");
const columnViewRef = ref<HTMLElement | null>(null);

const normalizeModelValue = (
  value: ApiObjectWithReferences | null
): ApiObjectWithReferences | null => {
  if (!value) {
    return null;
  }
  return api.byId(value.object.id)?.data ?? value;
};

const chosenContainer = ref<ApiObjectWithReferences | null>(
  normalizeModelValue(props.modelValue)
);

function buildPathTo(id: number): number[] {
  const path: number[] = [id];
  let parentId = api.byId(id)?.parentID ?? null;
  while (parentId !== null) {
    path.unshift(parentId);
    parentId = api.byId(parentId)?.parentID ?? null;
  }
  return path;
}

function initPathFromValue(value: ApiObjectWithReferences | null): number[] {
  const normalized = normalizeModelValue(value);
  const id = normalized?.object?.id;
  if (id == null) {
    return [];
  }
  return buildPathTo(id);
}

const selectedPath = ref<number[]>(initPathFromValue(props.modelValue));

watch(
  () => props.modelValue,
  (newValue) => {
    chosenContainer.value = normalizeModelValue(newValue);
    selectedPath.value = initPathFromValue(newValue);
  }
);

const availableContainers = computed<ApiObjectWithReferences[]>(() =>
  api.containers.filter(
    (item) => !props.excludeIds.includes(item.object.id)
  )
);

const filteredContainers = computed<ApiObjectWithReferences[]>(() =>
  search.value
    ? availableContainers.value.filter(
      (item: ApiObjectWithReferences) => filterContainers(item, search.value)
    )
    : availableContainers.value
);
const filteredContainersWithId = computed(() =>
  filteredContainers.value.map((container) =>
    Object.assign({ id: container.object.id }, container)
  )
);

const rootContainers = computed<ApiObjectWithReferences[]>(() =>
  api.objectsAtRoot
    .filter((w) => w.data.object.type === OBJECT_TYPE_CONTAINER)
    .map((w) => w.data)
    .filter((item) => !props.excludeIds.includes(item.object.id))
);

function hasChildren(container: ApiObjectWithReferences): boolean {
  const id = container.object.id;
  const wrapper = api.byId(id);
  return (wrapper?.children ?? []).some(
    (child) =>
      child.object.type === OBJECT_TYPE_CONTAINER
      && !props.excludeIds.includes(child.object.id)
  );
}

const columns = computed<ApiObjectWithReferences[][]>(() => {
  const result: ApiObjectWithReferences[][] = [rootContainers.value];
  for (const id of selectedPath.value) {
    const wrapper = api.byId(id);
    const children = (wrapper?.children ?? []).filter(
      (child) =>
        child.object.type === OBJECT_TYPE_CONTAINER
        && !props.excludeIds.includes(child.object.id)
    );
    if (children.length === 0) {
      break;
    }
    result.push(children);
  }
  return result;
});

watch(
  () => columns.value.length,
  async () => {
    await nextTick();
    if (columnViewRef.value) {
      columnViewRef.value.scrollTo({
        left: columnViewRef.value.scrollWidth,
        behavior: "smooth",
      });
    }
  }
);

function selectAtLevel(level: number, container: ApiObjectWithReferences) {
  selectedPath.value = [...selectedPath.value.slice(0, level), container.object.id];
  chosenContainer.value = container;
}

function selectRoot() {
  chosenContainer.value = null;
  selectedPath.value = [];
}

function doneButton() {
  const value = chosenContainer.value;
  emit("update:modelValue", value);
  emit("save", value);
}

function cancelButton() {
  emit("close");
}
</script>

<style scoped lang="scss">
.description {
  color: dimgray;
}

.error {
  color: var(--ion-color-danger, #c62828);
}

.modal-copy {
  p {
    margin-top: 0;
    margin-bottom: 0.75rem;
  }
}

.tree-copy {
  padding-bottom: 0.25rem;
}

.root-selector {
  padding-bottom: 0.75rem;
}

.root-item {
  margin-bottom: 0.5rem;
}

.root-pill {
  border: 1px solid var(--ion-border-color, rgba(0, 0, 0, 0.12));
  border-radius: 12px;
}

.footer-action {
  margin: 8px 16px calc(8px + var(--ion-safe-area-bottom, 0px));
}

.scroller {
  height: 100%;

  .item-inner {
    height: 60px;
  }
}

.column-view {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;
  height: calc(100vh - 164px);
  min-height: 300px;
}

.column {
  min-width: 220px;
  flex-shrink: 0;
  overflow-y: auto;
  height: 100%;
  border-right: 1px solid var(--ion-border-color, rgba(0, 0, 0, 0.12));

  &:last-child {
    border-right: none;
  }
}

.column-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--ion-border-color, rgba(0, 0, 0, 0.06));
  transition: background 0.15s;

  &:hover {
    background: var(--ion-color-light, #f4f5f8);
  }

  &.is-selected {
    background: var(--ion-color-primary, #3880ff);
    color: white;

    .chevron {
      color: white;
    }
  }

  .item-icon {
    flex-shrink: 0;
    font-size: 16px;
    width: 20px;
    text-align: center;
  }

  .item-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
  }

  .chevron {
    flex-shrink: 0;
    font-size: 14px;
    color: var(--ion-color-medium, #92949c);
  }
}
</style>
